import { motion, AnimatePresence } from 'framer-motion';
import { AlertCircle, ChevronRight, ChevronDown, ArrowRight, Lock, Upload as UploadIcon, FileText } from 'lucide-react';
import { useState, useEffect } from 'react';
import PortalShell from '@/components/PortalShell';
import AutoCursor from '@/components/AutoCursor';
import TypedText from '@/components/TypedText';

const moduleOptions = ['Manage', 'Mandatory Tariff', 'Resources', 'Transactions'];

export default function ReportFormFilledScene({ onNext }) {
  const [moduleOpen, setModuleOpen] = useState(false);
  const [moduleSelected, setModuleSelected] = useState(false);
  const [showSubject, setShowSubject] = useState(false);
  const [showFile, setShowFile] = useState(false);
  const [showDesc, setShowDesc] = useState(false);

  useEffect(() => {
    const timers = [];
    timers.push(setTimeout(() => setModuleOpen(true),     1800));
    timers.push(setTimeout(() => setModuleSelected(true), 3000));
    timers.push(setTimeout(() => setModuleOpen(false),    3400));
    timers.push(setTimeout(() => setShowSubject(true),    3800));
    timers.push(setTimeout(() => setShowFile(true),       5000));
    timers.push(setTimeout(() => setShowDesc(true),       5800));
    return () => timers.forEach(clearTimeout);
  }, []);

  const cursorPath = [
    { x: 500, y: 120, delay: 0.6 },                 // heading
    { x: 400, y: 300, delay: 1.4 },                 // skim prefilled name/email
    { x: 700, y: 300, delay: 1.2 },
    { x: 500, y: 460, delay: 1.2 },                 // Module
    { x: 500, y: 620, delay: 2.0 },                 // Subject
    { x: 500, y: 780, delay: 2.2 },                 // Description
    { x: 560, y: 920, delay: 2.0, click: true },    // Submit
  ];

  return (
    <PortalShell
      activeItem="User"
      reportActive
      overlay={<AutoCursor targets={cursorPath} startDelay={0.4} />}
    >
      <div data-testid="scene-report-form-filled" className="flex flex-col items-center justify-start h-full px-8 pt-4 overflow-y-auto">
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="w-full max-w-3xl pb-6">
          <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="text-center mb-4">
            <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-body font-medium bg-teal-600/10 text-teal-700 mb-3">
              <AlertCircle size={14} /> Report Issue
            </span>
            <h2 className="font-heading font-bold text-3xl tracking-tight text-[#0A0A0A]">Report Issue</h2>
            <p className="mt-1.5 font-body text-sm text-[#737373]">Select a module, describe the issue and submit</p>
          </motion.div>

          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.15 }}
            className="text-[12px] font-body text-[#737373] mb-4">
            Home <ChevronRight size={12} className="inline" /> <span className="font-medium text-[#0A0A0A]">Report Issue</span>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}
            className="bg-white rounded-2xl border border-black/5 shadow-xl p-6">

            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading font-bold text-lg text-[#0A0A0A]">Basic details</h3>
              <span className="inline-flex items-center gap-1.5 text-[11px] font-body text-teal-700 bg-teal-50 border border-teal-200 rounded-full px-2.5 py-1">
                <Lock size={11} /> Pre-filled from your profile
              </span>
            </div>

            {/* Name + Email — read-only pre-filled */}
            <div className="grid grid-cols-2 gap-x-5 gap-y-4 mb-4">
              <div>
                <label className="text-[13px] font-body font-bold text-[#0A0A0A] mb-1.5 block">Name</label>
                <div className="w-full px-3 py-2.5 rounded-lg border border-[#E5E7EB] bg-[#FAFAFA] text-sm font-body text-[#374151] flex items-center justify-between">
                  <span>Abdul Ahmed</span>
                  <Lock size={12} className="text-[#9CA3AF]" />
                </div>
              </div>
              <div>
                <label className="text-[13px] font-body font-bold text-[#0A0A0A] mb-1.5 block">Email address</label>
                <div className="w-full px-3 py-2.5 rounded-lg border border-[#E5E7EB] bg-[#FAFAFA] text-sm font-body text-[#374151] flex items-center justify-between">
                  <span>abdulahmed@abc.com</span>
                  <Lock size={12} className="text-[#9CA3AF]" />
                </div>
              </div>
            </div>

            {/* Module dropdown — animated open/select */}
            <div className="mb-5 relative">
              <label className="text-[13px] font-body font-bold text-[#0A0A0A] mb-1.5 block">Module<span className="text-red-500">*</span></label>
              <motion.div
                animate={{
                  borderColor: moduleOpen ? '#0D9488' : (moduleSelected ? '#0D9488' : '#E5E7EB'),
                  boxShadow: moduleOpen ? '0 0 0 3px rgba(13,148,136,0.18)' : '0 0 0 0 transparent',
                }}
                className="w-1/2 px-3 py-2.5 rounded-lg border bg-white text-sm font-body text-[#0A0A0A] flex items-center justify-between"
              >
                {moduleSelected
                  ? <motion.span initial={{ opacity: 0, y: -4 }} animate={{ opacity: 1, y: 0 }} className="font-medium">Mandatory Tariff</motion.span>
                  : <span className="text-[#A3A3A3]">Select module…</span>}
                <ChevronDown size={16} className="text-[#737373]" />
              </motion.div>
              <AnimatePresence>
                {moduleOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: -5 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -5 }}
                    className="absolute z-10 w-1/2 mt-1 bg-white rounded-lg border border-[#E5E7EB] shadow-xl overflow-hidden"
                  >
                    {moduleOptions.map(opt => (
                      <div
                        key={opt}
                        className={`px-3 py-2.5 text-sm font-body cursor-pointer ${
                          opt === 'Mandatory Tariff' && moduleSelected
                            ? 'bg-teal-50 text-teal-700 font-medium'
                            : 'text-[#374151] hover:bg-[#FAFAFA]'
                        }`}
                      >
                        {opt}
                      </div>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            <div className="border-t border-[#E5E7EB] my-4" />

            <h3 className="font-heading font-bold text-lg text-[#0A0A0A] mb-4">Complaint</h3>

            {/* Subject + File */}
            <div className="grid grid-cols-2 gap-x-5 gap-y-4 mb-4">
              <div>
                <label className="text-[13px] font-body font-bold text-[#0A0A0A] mb-1.5 block">Subject<span className="text-red-500">*</span></label>
                <motion.div
                  animate={showSubject ? { borderColor: ['#E5E7EB', '#0D9488', '#E5E7EB'] } : {}}
                  transition={{ duration: 0.8 }}
                  className="w-full px-3 py-2.5 rounded-lg border border-[#E5E7EB] bg-white text-sm font-body text-[#0A0A0A] min-h-[42px] flex items-center"
                >
                  {showSubject
                    ? <TypedText text="Report export timeout error" delay={0} speed={22} />
                    : <span className="text-[#A3A3A3]">Enter subject</span>}
                </motion.div>
              </div>
              <div>
                <label className="text-[13px] font-body font-bold text-[#0A0A0A] mb-1.5 block">File</label>
                <div className="flex gap-2">
                  <div className="flex-1 px-3 py-2.5 rounded-lg border border-[#E5E7EB] bg-white text-sm font-body min-h-[42px] flex items-center">
                    {showFile
                      ? <motion.span initial={{ opacity: 0, y: -4 }} animate={{ opacity: 1, y: 0 }} className="flex items-center gap-1.5 text-teal-700">
                          <FileText size={13} /> error_screenshot.pdf
                        </motion.span>
                      : <span className="text-[#A3A3A3]">Choose file</span>}
                  </div>
                  <button className="px-3 py-2.5 rounded-lg border border-[#E5E7EB] text-sm font-body font-medium text-[#374151] bg-white flex items-center gap-1.5">
                    <UploadIcon size={13} /> Upload
                  </button>
                </div>
              </div>
            </div>

            {/* Description */}
            <div>
              <label className="text-[13px] font-body font-bold text-[#0A0A0A] mb-1.5 block">Description<span className="text-red-500">*</span></label>
              <div className="w-full px-3 py-3 rounded-lg border border-[#E5E7EB] bg-white text-sm font-body text-[#0A0A0A] min-h-[80px] leading-relaxed">
                {showDesc
                  ? <TypedText
                      text="We are experiencing intermittent errors when generating reports in the Mandatory Tariff module. The export function returns a timeout error after 30 seconds. This affects all facility admin users in our organization."
                      delay={0}
                      speed={14}
                    />
                  : <span className="text-[#A3A3A3]">Describe the issue…</span>}
              </div>
              <p className="text-[11px] font-body text-[#737373] mt-1">Max 1,000 characters</p>
            </div>
          </motion.div>

          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.9 }} className="flex justify-center mt-5 mb-6">
            <motion.button
              data-testid="report-submit-button"
              onClick={onNext}
              animate={{
                scale: [1, 1, 1.1, 0.97, 1.06, 1],
                boxShadow: [
                  '0 0 0 0 rgba(13,148,136,0)',
                  '0 0 0 0 rgba(13,148,136,0)',
                  '0 0 0 10px rgba(13,148,136,0.3)',
                  '0 0 0 0 rgba(13,148,136,0)',
                  '0 0 0 8px rgba(13,148,136,0.25)',
                  '0 0 0 0 rgba(13,148,136,0)',
                ],
              }}
              transition={{ duration: 2.4, delay: 9.0, times: [0, 0.2, 0.4, 0.6, 0.8, 1] }}
              className="flex items-center gap-2 px-8 py-3 rounded-xl bg-teal-600 text-white font-body font-bold text-sm cursor-pointer"
            >
              Submit <ArrowRight size={16} />
            </motion.button>
          </motion.div>
        </motion.div>
      </div>
    </PortalShell>
  );
}
